import { Dispatch } from "redux"
import { ValidationError } from "sequelize"
import {
    CcRequirement,
    CcRequirementCreate,
    CcRequirementSave,
    CcRequirementParentChild,
} from "server/models"
import { NONE, VALUE, UNLIMITED, INFINITE, ASC } from "const"
import { OrderType } from "client/utils"
import {
    GET, POST, DELETE, validate, search, sort,
} from "client/store/utils"
import {
    LangType,
    PackageSettings,
    RequirementList,
    RequirementValidation,
    RequirementFilters,

    LoadCcRequirementTypes,
    LoadCcRequirementPriorities,
    LoadCcRequirementStatuses,
    LoadCcRequirementReviewMethods,
    LoadCcRequirementSmartCompliances,

    SortCcRequirementTypes,
    SortCcRequirementReviewMethods,
    SortCcRequirementSmartCompliance,

    setCcRequirementTypeList,
    setCcRequirementTypeIds,
    setCcRequirementPriorityList,
    setCcRequirementPriorityIds,
    setCcRequirementStatusList,
    setCcRequirementStatusIds,
    setCcRequirementReviewMethodList,
    setCcRequirementReviewMethodIds,
    setCcRequirementSmartComplianceList,
    setCcRequirementSmartComplianceIds,
} from "client/store"
import { TableNestedIds, TableGroupedIds } from "client/components"

export const LoadAllCcRequirements = (projectId: number) => (
    GET(`/ccRequirement/projectId/${projectId}`)
    .then((requirements: CcRequirement[]) => {
        const requirementList: RequirementList = {}
        requirements.forEach((requirement) => {
            requirementList[requirement.id] = {
                ...requirement,
                reviewMethodsParsed: requirement.reviewMethods ? JSON.parse(requirement.reviewMethods) : null,
                smartComplianceParsed: requirement.smartCompliance ? JSON.parse(requirement.smartCompliance) : null,
            }
        })
        return requirementList
    })
)

export const PrepareRequirementsNestedIds = (
    requirements: readonly CcRequirement[], // filtered and sorted list
    parentChild: readonly CcRequirementParentChild[],
) => {
    const groupedByParent: { [parentId: number ]: number[] } = {}
    parentChild.forEach((row) => {
        const { parentId, childId } = row
        if (!groupedByParent[parentId]) groupedByParent[parentId] = []
        groupedByParent[parentId].push(childId)
    })

    const getNested = (parentId: number, stackTrace: number[]) => {
        const nestedIds: TableNestedIds[] = []
        const childIds = groupedByParent[parentId]

        if (childIds) {
            const sortedChildren = requirements.filter((row) => (
                childIds.includes(row.id)
            ))
            sortedChildren.forEach((row) => {
                let nestedId: TableNestedIds
                if (stackTrace.includes(row.id)) {
                    nestedId = {
                        id: row.id,
                        nestedIds: [{
                            id: INFINITE,
                            nestedIds: [],
                        }],
                    }
                } else {
                    nestedId = {
                        id: row.id,
                        nestedIds: getNested(row.id, stackTrace.concat(row.id)),
                    }
                }
                nestedIds.push(nestedId)
            })
        }
        return nestedIds
    }

    const nestedIds: TableNestedIds[] = []
    requirements.forEach((requirement) => {
        const nestedId: TableNestedIds = {
            id: requirement.id,
            nestedIds: getNested(requirement.id, [requirement.id]),
        }
        nestedIds.push(nestedId)
    })

    const tableGrouped: TableGroupedIds[] = nestedIds.length
        ? [{
            groupId: null, // no grouping
            nestedIds,
            nestedGroupedIds: [],
        }]
        : []

    return tableGrouped
}

export const LoadCcRequirementById = (requirementId: number, projectId: number) => (
    GET(`/ccRequirement/id/${requirementId}/projectId/${projectId}`)
    .then((requirement: CcRequirement): CcRequirement => ({
        ...requirement,
        reviewMethodsParsed: requirement.reviewMethods ? JSON.parse(requirement.reviewMethods) : null,
        smartComplianceParsed: requirement.smartCompliance ? JSON.parse(requirement.smartCompliance) : null,
    }))
)

type SaveCcRequirementResponse = {
    newRequirementId: number
    validation: RequirementValidation
}
export const SaveCcRequirement = (
    id: number,
    requirement: CcRequirementCreate,
    maxRequirementNumber: number,
    lang: LangType,
) => (
    LoadAllCcRequirements(requirement.projectId).then((requirementList) => {
        if (!id && maxRequirementNumber !== null && Object.values(requirementList).length >= maxRequirementNumber) {
            return <SaveCcRequirementResponse>{
                newRequirementId: null,
                validation: <RequirementValidation>{
                    uniqueId: lang.maxRequirementsReached.replace("{0}", maxRequirementNumber.toString()),
                },
            }
        }

        const data: CcRequirementSave = {
            id,
            requirement,
        }
        return POST(
            "/ccRequirement/save",
            JSON.stringify(data),
        ).then((response) => {
            const newRequirement = response as CcRequirement
            const error = response as ValidationError

            return <SaveCcRequirementResponse>{
                newRequirementId: newRequirement.id || null,
                validation: <RequirementValidation>validate(error, lang),
            }
        })
    })
)

export const DeleteCcRequirement = (requirementId: number, projectId: number) => (
    DELETE(`/ccRequirement/delete/${requirementId}/${projectId}`)
    .then((removedRows: number) => removedRows)
)

export const SortCcRequirement = (
    requirements: CcRequirement[],
    sortColumn: keyof CcRequirement,
    sortOrder: OrderType,
    filters: RequirementFilters,
) => {
    const requirementsFiltered = requirements.filter((requirement) => {
        let showRow = true
        if (filters.uniqueId && requirement.uniqueId.toUpperCase().indexOf(filters.uniqueId.toUpperCase()) < 0) showRow = false
        else if (filters.name && requirement.name.toUpperCase().indexOf(filters.name.toUpperCase()) < 0) showRow = false
        else if (filters.creator && requirement.creator !== filters.creator) showRow = false

        else if (filters.typeId && requirement.typeId !== filters.typeId) showRow = false
        else if (filters.priorityId && requirement.priorityId !== filters.priorityId) showRow = false
        else if (filters.statusId && requirement.statusId !== filters.statusId) showRow = false

        else if (filters.search) showRow = search(filters.search, requirement)

        if (filters.reviewMethods) {
            Object.keys(filters.reviewMethods).forEach((index) => {
                const methodId = Number(index)
                if (filters.reviewMethods[methodId] && !requirement.reviewMethodsParsed[methodId]) showRow = false
            })
        }
        if (filters.smartCompliances) {
            Object.keys(filters.smartCompliances).forEach((index) => {
                const complianceId = Number(index)
                if (filters.smartCompliances[complianceId] && !requirement.smartComplianceParsed[complianceId]) showRow = false
            })
        }
        return showRow
    })

    return requirementsFiltered.sort((a, b) => sort(a, b, sortColumn, sortOrder))
}

export const GetMaxCcRequirements = (
    packageSettings: PackageSettings,
    requirementsNumber?: number,
    lang?: LangType,
) => {
    const { ccRequirements, ccRequirementsValue } = packageSettings

    let maxRequirements = 0
    if (ccRequirements === NONE) maxRequirements = 0
    if (ccRequirements === VALUE && ccRequirementsValue) maxRequirements = ccRequirementsValue
    if (ccRequirements === UNLIMITED) maxRequirements = null

    // feedbacks
    let feedback: string = ""
    if (lang) {
        if (maxRequirements === null) {
            feedback = lang.maxRequirementsUnlimited
        } else {
            if (requirementsNumber < maxRequirements) feedback = lang.maxRequirements
            if (requirementsNumber === maxRequirements) feedback = lang.maxRequirementsReached
            if (requirementsNumber > maxRequirements) feedback = lang.maxRequirementsExceeded
            feedback = feedback.replace("{0}", maxRequirements.toString())
        }
    }

    return {
        maxRequirements,
        requirementFeedback: feedback,
    }
}

export const LoadCcRequirementDictionaries = (accountId: number, projectId: number, dispatch: Dispatch) => (
    Promise.all([
        LoadCcRequirementTypes(accountId, projectId).then((res) => {
            const sortedIds = SortCcRequirementTypes(Object.values(res), "name", ASC, {})
            dispatch(setCcRequirementTypeList(res))
            dispatch(setCcRequirementTypeIds(sortedIds))
        }),
        LoadCcRequirementPriorities(accountId, projectId).then((res) => {
            dispatch(setCcRequirementPriorityList(res.priorityList))
            dispatch(setCcRequirementPriorityIds(res.priorityIds))
        }),
        LoadCcRequirementStatuses(accountId, projectId).then((res) => {
            dispatch(setCcRequirementStatusList(res.statusList))
            dispatch(setCcRequirementStatusIds(res.statusIds))
        }),
        LoadCcRequirementReviewMethods(accountId, projectId).then((res) => {
            const sortedIds = SortCcRequirementReviewMethods(Object.values(res), "name", ASC, {})
            dispatch(setCcRequirementReviewMethodList(res))
            dispatch(setCcRequirementReviewMethodIds(sortedIds))
        }),
        LoadCcRequirementSmartCompliances(accountId, projectId).then((res) => {
            const sortedIds = SortCcRequirementSmartCompliance(Object.values(res), "name", ASC, {})
            dispatch(setCcRequirementSmartComplianceList(res))
            dispatch(setCcRequirementSmartComplianceIds(sortedIds))
        }),
    ])
)
